export interface LibraryRoot {
  name: string;
  path: string;
  ready: boolean;
}

export interface Health {
  status: string;
  version: string;
  library_roots: LibraryRoot[];
  /** True only when every root is present — see Config.library_ready. */
  library_ready: boolean;
  data_path: string;
  ffmpeg: boolean;
  bpm_range: [number, number];
  tempo_confidence_min: number;
  /** Longest region /audio will serve; anything longer is clamped server-side. */
  max_segment_seconds: number;
}

export interface Track {
  id: number;
  path: string;
  artist: string | null;
  title: string | null;
  album: string | null;
  duration: number | null;
  samplerate: number | null;
  channels: number | null;
  missing: boolean;
  /** Marked junk: hidden from the library and never auto-mixed. File untouched. */
  excluded: boolean;
  tags: string[];
  analysis_state: string | null;
  /** Effective BPM, or null when no tempo can be asserted. */
  tempo: number | null;
  tempo_source: "override" | "detected" | null;
  tempo_confidence: number | null;
  lufs: number | null;
  /** Linear auto-gain to a common loudness (A6); 1.0 when unmeasured. */
  gain: number;
}

export interface TrackPage {
  total: number;
  limit: number;
  offset: number;
  items: Track[];
}

export interface ScanStatus {
  running: boolean;
  started_at: number | null;
  finished_at: number | null;
  seen: number;
  added: number;
  updated: number;
  unchanged: number;
  restored: number;
  missing: number;
  errors: number;
  last_error: string | null;
  failed: string[];
}

export interface AnalysisStatus {
  running: boolean;
  current_id: number | null;
  current_path: string | null;
  pending: number;
  analysed: number;
  failed: number;
  last_error: string | null;
  version: number;
  analyzer: string;
}

export interface LibrarySummary {
  total: number;
  present: number;
  missing: number;
  excluded: number;
  analysed: number;
  scan: ScanStatus;
}

export interface Tag {
  id: number;
  name: string;
  /** How many *visible* tracks carry it — agrees with filtering by this tag. */
  track_count: number;
}

export interface PurgeRootStat {
  missing: number;
  total: number;
  share: number;
}

export interface PurgePlan {
  count: number;
  forced: boolean;
  per_root: Record<string, PurgeRootStat>;
  tracks: { id: number; root: string; path: string }[];
}

export interface PurgeResult {
  purged: number;
  peaks_removed: number;
  peaks_failed: number;
  per_root: Record<string, PurgeRootStat>;
}

export type SortKey = "artist" | "title" | "album" | "duration" | "added" | "bpm";

export interface TrackQuery {
  q?: string;
  sort?: SortKey;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  /** List the excluded (junk) tracks instead of the library. */
  excluded?: boolean;
  /** Only tracks carrying this tag. */
  tag?: string;
}

export class Unauthorized extends Error {
  constructor() {
    super("authentication required");
    this.name = "Unauthorized";
  }
}

/** Notified when any request comes back unauthenticated, so the UI can prompt. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      accept: "application/json",
      // Required by the server on state-changing requests. A cross-origin form
      // cannot set it, which is what closes CSRF.
      "x-requested-with": "web-library-dj",
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new Unauthorized();
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export interface AuthState {
  required: boolean;
  authenticated: boolean;
}

export const fetchAuthState = () => request<AuthState>("/api/auth");

export const login = (password: string) =>
  request<{ authenticated: boolean }>("/api/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });

export const logout = () => request<{ authenticated: boolean }>("/api/logout", { method: "POST" });

export const fetchHealth = () => request<Health>("/api/health");

export const fetchLibrary = () => request<LibrarySummary>("/api/library");

export const fetchScanStatus = () => request<ScanStatus>("/api/scan/status");

export const startScan = () => request<{ started: boolean }>("/api/scan", { method: "POST" });

export const fetchAnalysisStatus = () => request<AnalysisStatus>("/api/analysis/status");

/** Set a manual tempo, or pass null to clear it and fall back to detection. */
export const setBpmOverride = (trackId: number, bpm: number | null) =>
  request<Track>(`/api/tracks/${trackId}/bpm`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ bpm }),
  });

// --- curation --------------------------------------------------------------

const jsonBody = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

/**
 * Mark a track as junk, or put it back.
 *
 * Index-only: the file is never touched. Use `excludedPathsUrl` to get the list
 * of paths if you want to delete the bytes, which stays a manual step.
 */
export const setExcluded = (trackId: number, excluded: boolean) =>
  request<Track>(`/api/tracks/${trackId}/excluded`, jsonBody("PUT", { excluded }));

export const excludedPathsUrl = "/api/excluded/paths";

export const fetchTags = () => request<{ tags: Tag[] }>("/api/tags").then((r) => r.tags);

export const addTrackTag = (trackId: number, name: string) =>
  request<Track>(`/api/tracks/${trackId}/tags`, jsonBody("PUT", { name }));

export const removeTrackTag = (trackId: number, name: string) =>
  request<Track>(`/api/tracks/${trackId}/tags/${encodeURIComponent(name)}`, { method: "DELETE" });

export const renameTag = (tagId: number, name: string) =>
  request<Tag>(`/api/tags/${tagId}`, jsonBody("PATCH", { name }));

export async function deleteTag(tagId: number): Promise<void> {
  // 204, so there is no JSON body to parse — `request` would choke on it.
  const res = await fetch(`/api/tags/${tagId}`, {
    method: "DELETE",
    headers: { "x-requested-with": "web-library-dj" },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
}

export const fetchPurgePlan = (force = false) =>
  request<PurgePlan>(`/api/purge/plan?force=${force}`);

/**
 * `expectCount` is what the dry run showed. The server refuses if the real
 * number has changed since, so a stale confirmation cannot delete more than
 * was on screen when you agreed to it.
 */
export const runPurge = (expectCount: number, force = false) =>
  request<PurgeResult>("/api/purge", jsonBody("POST", { force, expect_count: expectCount }));

export function peaksUrl(trackId: number, res: "overview" | "detail"): string {
  return `/api/tracks/${trackId}/peaks?res=${res}`;
}

export function fetchTracks(query: TrackQuery = {}): Promise<TrackPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return request<TrackPage>(`/api/tracks?${params}`);
}
